import { ProblemTypes } from "@faqrun/shared";
import { getConfig } from "@/config/app.config";
import { getCsrfToken } from "../csrf";
import { ApiClientError } from "./api-error";
import type { ProblemDetails } from "./problem-details";

const API_BASE_URL = getConfig().apiUrl;

/** JSON 以外のバイナリレスポンス（CSV, Excel, PDF 等）かどうかを判定 */
function isBinaryContentType(contentType: string): boolean {
  const type = contentType.split(";")[0]?.trim().toLowerCase() ?? "";
  return (
    type === "text/csv" ||
    type === "application/octet-stream" ||
    type === "application/pdf" ||
    type.startsWith("application/vnd.")
  );
}

// module-scope singleflight: 並列 401 が起きても /refresh は同時 1 回しか走らない
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (refreshInFlight === null) {
    refreshInFlight = fetch(`${API_BASE_URL}/api/admin/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// リフレッシュ + リトライの共通ロジック
async function tryRefreshAndRetry(
  fullUrl: string,
  options: RequestInit,
  headers: Headers,
): Promise<Response | null> {
  const refreshed = await refreshOnce();

  if (!refreshed) {
    return null; // リフレッシュ失敗
  }

  // Cookie は Set-Cookie で自動更新される
  const newCsrfToken = getCsrfToken();
  if (newCsrfToken) {
    headers.set("X-CSRF-Token", newCsrfToken);
  }
  return fetch(fullUrl, { ...options, headers, credentials: "include" });
}

export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;

  const headers = new Headers(options.headers);

  // multipart/form-data の場合は Content-Type を設定しない（ブラウザが自動設定）
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // CSRF トークンを付与（state-changing リクエスト用）
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers,
    credentials: "include", // Cookie 自動送信
  });

  // 401: 認証切れ → リフレッシュ試行
  if (response.status === 401) {
    const retryResponse = await tryRefreshAndRetry(fullUrl, options, headers);
    if (retryResponse) {
      response = retryResponse;
    } else {
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      throw new ApiClientError(401, {
        title: "認証エラー",
        status: 401,
        detail: "セッションの有効期限が切れました",
      });
    }
  }

  // 403: CSRF token 切れの場合はリフレッシュ試行、それ以外は権限エラー
  if (response.status === 403) {
    const cloned = response.clone();
    const json403 = await cloned.json();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- error response parsing
    const data403 = json403 as ProblemDetails;

    if (data403.type === ProblemTypes.CSRF_TOKEN_MISMATCH) {
      // CSRF token が access_token と同時に期限切れ → リフレッシュで再取得
      const retryResponse = await tryRefreshAndRetry(fullUrl, options, headers);
      if (retryResponse) {
        // リトライ後も認証切れなら /login へ（直接 401 を受けた場合と同じ挙動に揃える）
        if (retryResponse.status === 401) {
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          throw new ApiClientError(401, {
            title: "認証エラー",
            status: 401,
            detail: "セッションの有効期限が切れました",
          });
        }
        response = retryResponse;
      } else {
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        throw new ApiClientError(403, {
          title: "認証エラー",
          status: 403,
          detail: "セッションの有効期限が切れました",
        });
      }
    } else {
      throw new ApiClientError(403, {
        title: data403.title ?? "権限エラー",
        status: 403,
        detail: data403.detail ?? "この操作を行う権限がありません",
      });
    }
  }

  if (response.status === 204) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generic boundary
    return { data: undefined, status: response.status, headers: response.headers } as T;
  }

  // バイナリレスポンス（CSV 等）の場合は Blob として返す
  const contentType = response.headers.get("Content-Type") ?? "";
  if (isBinaryContentType(contentType)) {
    if (!response.ok) {
      throw new ApiClientError(response.status, {
        title: "ダウンロードエラー",
        status: response.status,
        detail: `ファイルの取得に失敗しました: ${response.status}`,
      });
    }
    const blob = await response.blob();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generic boundary
    return { data: blob, status: response.status, headers: response.headers } as T;
  }

  const json = await response.json();

  if (!response.ok) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- error response parsing
    const data = json as ProblemDetails;
    // Problem Details の errors 配列（バリデーションエラー）からメッセージを結合
    const errors = Array.isArray(data.errors)
      ? data.errors.map((e) => e.message).join(" / ")
      : undefined;
    throw new ApiClientError(response.status, {
      type: data.type,
      title: data.title ?? "エラーが発生しました",
      status: response.status,
      detail: errors ?? data.detail,
    });
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generic boundary
  return { data: json, status: response.status, headers: response.headers } as T;
}

/**
 * @deprecated 直接呼び出し禁止。orval生成関数（faqrunAPI.ts）を使用すること。
 * customFetch はorval生成コード（mutator）用の内部ヘルパーである。
 * 新規コードでは必ず orval 生成関数経由で API を呼び出せ。
 */
export async function fetchData<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await customFetch<{ data: T; status: number; headers: Headers }>(url, options);
  return res.data;
}
